import * as React from 'react';
import i18n from '@i18next/i18n';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CommentGroupInfoMap, IAddCommentGroup } from '../../../base/index.interface';
import { ICommentApi, PendingCommentGroup } from '../../../type';
import CommentGroup from '../comment-group';
import CommentDrawerEmpty from './comment-drawer-empty';
import Style from '../index.less';

interface ICommentDrawerProps extends ICommentApi {
    totalCommentGroupSize: number;
    commentGroupInfoMap: CommentGroupInfoMap | undefined;
    activeGroupId: string | undefined;
    hoverCommentGroupIds?: string[];
    selectionService?: ISelectionService;
    isAddCommentDisabled?: boolean;
    isJumpBtnDisabled?: boolean;
    setActiveGroupId(activeGroupId: string | undefined): void;
    setHoverGroupId(hoverGroupId: string | undefined): void;
    addCommentGroup(): IAddCommentGroup | undefined;
    jumpToNextComment(): void;
    changeCurrentSlideByActiveChange(isPrev?: boolean): void;
    setPendingCommentGroup(pendingCommentGroup: PendingCommentGroup): void;
}

const DEFAULT_ACTIVE_INDEX = 0;

export const CommentDrawer = (props: ICommentDrawerProps, ref: any) => {
    const commentDrawerRef = useRef<any>();
    const groupIdListRef = useRef<string[]>([]);
    const { selectionService } = props;

    const [activeGroupId, setActiveGroupId] = useState(props.activeGroupId ?? '');
    const [commentGroupInfoMap, setCommentGroupInfoMap] = useState(props.commentGroupInfoMap);
    const [pendingCommentGroup, setPendingCommentGroup] = useState<PendingCommentGroup>(undefined);
    const [totalCommentGroupSize, setTotalCommentGroupSize] = useState(props.totalCommentGroupSize);
    const [hoverCommentGroupIds, setHoverCommentGroupIds] = useState(props.hoverCommentGroupIds);
    const [isAddCommentDisabled, setIsAddCommentDisabled] = useState(props.isAddCommentDisabled);
    const [isJumpBtnDisabled, setIsJumpBtnDisabled] = useState(props.isJumpBtnDisabled);

    const commentGroupInfoSize = commentGroupInfoMap?.size ?? 0;

    useEffect(() => {
        groupIdListRef.current = [];
        if (commentGroupInfoSize === 0) return;
        const pendingGroupId = pendingCommentGroup?.groupId;
        commentGroupInfoMap?.forEach((commentGroupInfoList, groupId) => {
            if (pendingGroupId === groupId) {
                handleActiveIdChange(pendingGroupId);
            }
            groupIdListRef.current.push(groupId);
        });
    }, [commentGroupInfoMap]);

    const handleActiveIdChange = (groupId: string) => {
        if (groupId === activeGroupId) return;
        setActiveGroupId(groupId);
        props.setActiveGroupId(groupId);
        // 清除pendingGroupId，即清除插入的空批注
        const pendingGroupId = pendingCommentGroup?.groupId;
        if (pendingGroupId && groupId !== pendingGroupId) {
            setPendingCommentGroup(undefined);
            props.setPendingCommentGroup(undefined);
            if (commentGroupInfoMap?.get(pendingGroupId)) {
                commentGroupInfoMap.delete(pendingGroupId);
            }
        }
    };

    const updateHoverCommentGroupIds = (hoverCommentGroupIds: string[] | undefined) => {
        setHoverCommentGroupIds(hoverCommentGroupIds);
    };

    useImperativeHandle(ref, () => ({
        updateCommentDrawerProps,
        updateActiveGroupId,
        updateHoverCommentGroupIds,
        scrollToView,
        updateBtnState,
    }));

    const updateCommentDrawerProps = ({ commentGroupInfoMap, pendingCommentGroup, totalCommentGroupSize }) => {
        // 重新构造一个map，避免不会触发重新渲染
        const newCommentGroupInfoMap = commentGroupInfoMap ? new Map(commentGroupInfoMap) : commentGroupInfoMap;
        setCommentGroupInfoMap(newCommentGroupInfoMap);
        setPendingCommentGroup(pendingCommentGroup);
        if (typeof totalCommentGroupSize === 'number') {
            setTotalCommentGroupSize(totalCommentGroupSize);
        }
    };

    /**
     * 更新当前激活的批注组：1.切换选中元素时，激活当前元素的第一个批注。 2.通过点击上一个/下一个批注按钮跳转批注。
     * @param payload
     * @returns
     */
    const updateActiveGroupId = (payload: { isJumpToPrevComment?: boolean, activeGroupId?: string }) => {
        // 1.切换选中元素的方式更新activeGroupId
        if (typeof payload?.activeGroupId !== 'undefined') {
            handleActiveIdChange(payload.activeGroupId);
            return;
        }
        // 2.通过点击上一个/下一个批注按钮的方式更新activeGroupId
        if (groupIdListRef.current.length === 0) {
            // 切换到下一页
            changeSlide(DEFAULT_ACTIVE_INDEX);
            return;
        }
        const index = groupIdListRef.current.findIndex(id => id === activeGroupId);
        if (index === -1) {
            // 表明当前页没有找到，则默认激活当前页第一个批注
            handleActiveIdChange(groupIdListRef.current[0]);
            return;
        }
        const { length } = groupIdListRef.current;
        const newIndex = payload?.isJumpToPrevComment ? index - 1 : index + 1;
        if (newIndex >= length) {
            // 切换到下一页
            changeSlide(DEFAULT_ACTIVE_INDEX);
            return;
        } if (newIndex < 0) {
            // 切换到上一页
            changeSlide(length - 1, true);
            return;
        }
        handleActiveIdChange(groupIdListRef.current[newIndex]);
    };

    const changeSlide = (activeIndex: number, isPrev?: boolean) => {
        if (!selectionService) return;
        const { selectedSlides, slides, currentSlideIndex } = selectionService;
        let slideIndex = currentSlideIndex;
        do {
            if (isPrev) {
                slideIndex -= 1;
                if (slideIndex === -1) {
                    slideIndex = slides.length - 1;
                }
            } else {
                slideIndex += 1;
                if (slideIndex >= slides.length) {
                    slideIndex = 0;
                }
            }
            const commentGroupMapSize = slides[slideIndex].commentGroupMap?.size ?? 0;
            if (commentGroupMapSize > 0) {
                break;
            }
        } while (slideIndex !== currentSlideIndex);
        if (slideIndex === currentSlideIndex) {
            handleActiveIdChange(groupIdListRef.current[activeIndex]);
            return;
        }
        props.changeCurrentSlideByActiveChange(isPrev);
        selectedSlides.clear();
        selectionService.changeCurrentSlideIndexByUserAction(slideIndex);
        selectedSlides.add(slides[slideIndex]);
        selectionService.needClearSelectedSlideClearanceFlag += 1;
    };

    const scrollToView = () => {
        const scrollHeight = commentDrawerRef.current?.scrollHeight ?? 0;
        const clientHeight = commentDrawerRef.current?.clientHeight ?? 0;
        if (scrollHeight > clientHeight) {
            commentDrawerRef.current.scrollTo({ top: scrollHeight, behavior: 'smooth' });
        }
    };

    const updateBtnState = ({ isAddCommentDisabled, isJumpBtnDisabled }) => {
        if (typeof isAddCommentDisabled !== 'undefined') {
            setIsAddCommentDisabled(isAddCommentDisabled);
        }
        if (typeof isJumpBtnDisabled !== 'undefined') {
            setIsJumpBtnDisabled(isJumpBtnDisabled);
        }
    };

    const handleMouseEnter = (groupId: string) => {
        props.setHoverGroupId(groupId);
    };

    const handleMouseLeave = () => {
        props.setHoverGroupId('');
    };

    const renderCommentGroups = () => (
        <div
            className={Style['comment-group-wrapper']}
        >
            {commentGroupInfoMap && Array.from(commentGroupInfoMap).map(([groupId, commentGroupInfoList]) => (
                <CommentGroup
                    groupId={groupId}
                    commentGroupInfoList={commentGroupInfoList}
                    onActive={handleActiveIdChange}
                    isActive={activeGroupId === groupId}
                    addComment={props.addComment}
                    updateComment={props.updateComment}
                    deleteComment={props.deleteComment}
                    deleteCommentGroup={props.deleteCommentGroup}
                    onMouseOver={handleMouseEnter}
                    onMouseOut={handleMouseLeave}
                    isElementAnchorHover={hoverCommentGroupIds?.includes(groupId)}
                    key={groupId}
                />
            ))}
        </div>
    );


    return <div className={Style['comment-drawer']} ref={commentDrawerRef}>
        {
            (commentGroupInfoSize > 0)
                ? renderCommentGroups()
                : <CommentDrawerEmpty
                    isAddCommentDisabled={isAddCommentDisabled}
                    isJumpBtnDisabled={isJumpBtnDisabled}
                    addCommentGroup={props.addCommentGroup}
                    jumpToNextComment={props.jumpToNextComment}
                    updateCommentDrawerProps={updateCommentDrawerProps} />
        }
        <div className={Style['comment-footer']}>
            {i18n.t('当前页 {0} 条，全文档共 {1} 条', { 0: commentGroupInfoSize, 1: totalCommentGroupSize })}
        </div>
    </div>;
};

export default forwardRef(CommentDrawer);
